/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ============================================================
   GemPages / Third-party → Theme Cart Drawer Bridge

   The theme's <cart-drawer> opens when "cart:refresh" is dispatched
   on documentElement with detail.openMiniCart === true. GemPages
   add-to-cart buttons (and some third-party apps like Appstle quick
   add) POST to /cart/add.js but don't dispatch that event — so the
   drawer never opens and the existing drawer instance shows stale
   content until a full reload.

   This bridge intercepts fetch + XHR globally. When ANY successful
   /cart/add(.js) request completes, it dispatches the theme event
   so the drawer re-renders and slides in.

   Dedupe: the theme's own <form is="product-form"> already fires
   cart:refresh after add. We delay our dispatch by 150 ms; if the
   theme fires within that window, we skip ours.
   ============================================================ */
(function () {
  if (window.__dbCartBridge) return;
  window.__dbCartBridge = true;

  var ADD_RE    = /\/cart\/add(\.js)?(\?|$|#)/;
  var MUTATE_RE = /\/cart\/(change|update|clear)(\.js)?(\?|$|#)/;

  var lastThemeDispatch = 0;

  document.documentElement.addEventListener('cart:refresh', function () {
    lastThemeDispatch = Date.now();
  }, true);

  function scheduleRefresh(openDrawer) {
    var cfg = window.themeVariables && window.themeVariables.settings;
    if (cfg && cfg.cartType && cfg.cartType !== 'drawer') return;

    var scheduledAt = Date.now();
    setTimeout(function () {
      if (lastThemeDispatch >= scheduledAt) return; // theme already handled it
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { openMiniCart: !!openDrawer }
      }));
      lastThemeDispatch = Date.now();
    }, 150);
  }

  function extractUrl(input) {
    if (!input) return '';
    if (typeof input === 'string') return input;
    if (input.url) return input.url;
    try { return String(input); } catch (_) { return ''; }
  }

  /* ---------- fetch ---------- */
  if (typeof window.fetch === 'function') {
    var origFetch = window.fetch.bind(window);
    window.fetch = function () {
      var url = extractUrl(arguments[0]);
      var p = origFetch.apply(window, arguments);
      if (url && (ADD_RE.test(url) || MUTATE_RE.test(url))) {
        p.then(function (res) {
          if (res && res.ok) scheduleRefresh(ADD_RE.test(url));
        }).catch(function () {});
      }
      return p;
    };
  }

  /* ---------- XMLHttpRequest ---------- */
  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__dbCartUrl = url;
    return origOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var url = this.__dbCartUrl;
    if (typeof url === 'string' && (ADD_RE.test(url) || MUTATE_RE.test(url))) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) {
          scheduleRefresh(ADD_RE.test(url));
        }
      });
    }
    return origSend.apply(this, arguments);
  };
})();


/* ============================================================
   Appstle Widget: click anywhere on a plan card to select it.

   Appstle wires the change-event to its <input type="radio">. The
   only built-in clickable surface is the <label>. We delegate
   clicks at document level: if the click lands inside an option
   card but NOT on an existing interactive control (the interval
   <select>, the label itself, buttons or links), we forward the
   click to the card's label so the radio toggles naturally.
   ============================================================ */
(function () {
  if (window.__dbAppstleCardClick) return;
  window.__dbAppstleCardClick = true;

  document.addEventListener('click', function (event) {
    var card = event.target.closest('.appstle_subscription_wrapper_option');
    if (!card) return;

    // Skip clicks already targeting an interactive element
    if (event.target.closest(
      'label, input, select, option, button, a, ' +
      '.appstle_subscribe_option, .appstle_custom_select'
    )) return;

    var label = card.querySelector('.appstle_radio_label');
    if (label) label.click();
  }, true);
})();


/* ============================================================
   Appstle Widget: inject the "Ein Abo lohnt sich" benefits list.

   The widget is rendered by Appstle client-side, so we can't place
   markup inside it from Liquid. theme.liquid renders the block once
   into <template id="db-abo-benefits-template"> (heading + rich-text
   bullets + check icons, driven by theme settings). Here we clone it
   into the FIRST subscription card (after the price/title row, before
   the interval dropdown) and re-inject whenever Appstle re-renders the
   widget (variant/plan change wipes our node). The presence check
   keeps it idempotent, and a rAF coalesces observer bursts.
   ============================================================ */
(function () {
  if (window.__dbAppstleBenefits) return;
  window.__dbAppstleBenefits = true;

  var template = document.getElementById('db-abo-benefits-template');
  if (!template || !('content' in template)) return;

  function inject() {
    var widgets = document.querySelectorAll('[id^="appstle_subscription_widget"]');
    for (var i = 0; i < widgets.length; i++) {
      // First option card = the subscription plan (one-time is rendered after).
      var card = widgets[i].querySelector('.appstle_subscription_wrapper_option');
      if (!card || card.querySelector('.db-abo-benefits')) continue;

      var anchor = card.querySelector('.appstle_subscription_radio_wrapper');
      if (!anchor) continue;

      var block = template.content.firstElementChild.cloneNode(true);
      anchor.insertAdjacentElement('afterend', block);
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; inject(); });
  }

  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  if (document.readyState !== 'loading') inject();
  else document.addEventListener('DOMContentLoaded', inject);
})();


/* ============================================================
   Free-shipping bar: toggle `.shipping-bar--complete` class
   reactively when the cart is updated (server-side renders the
   initial state, this handles dynamic add/remove/qty changes).
   ============================================================ */
(function () {
  if (window.__dbShippingBarComplete) return;
  window.__dbShippingBarComplete = true;

  document.documentElement.addEventListener('cart:updated', function (event) {
    var bars = document.querySelectorAll('.shipping-bar');
    if (!bars.length) return;

    var items = (event && event.detail && event.detail.cart && event.detail.cart.items) || [];
    var total = items
      .filter(function (item) { return item.requires_shipping; })
      .reduce(function (sum, item) { return sum + item.final_line_price; }, 0);

    bars.forEach(function (bar) {
      var threshold = parseFloat(bar.getAttribute('threshold'));
      if (!isFinite(threshold) || threshold <= 0) return;
      bar.classList.toggle('shipping-bar--complete', total >= threshold);
    });
  });
})();